import { createContext, useContext, useState, useEffect } from 'react';

const PrivacyMaskContext = createContext();

export const MASK_MODES = {
  OFF: 'off',
  ON: 'on',
  STRICT: 'strict'
};

export const PrivacyMaskProvider = ({ children }) => {
  const [maskMode, setMaskMode] = useState(() => {
    const stored = localStorage.getItem('privacyMaskMode');
    return stored || MASK_MODES.OFF;
  });

  const [unmaskedIds, setUnmaskedIds] = useState(new Set());
  const [timeouts, setTimeouts] = useState(new Map());

  useEffect(() => {
    localStorage.setItem('privacyMaskMode', maskMode);
    // Clear unmasked IDs and timeouts when switching modes
    setUnmaskedIds(new Set());
    timeouts.forEach(timeout => clearTimeout(timeout));
    setTimeouts(new Map());
  }, [maskMode]);

  const toggleMaskMode = () => {
    const modes = [MASK_MODES.OFF, MASK_MODES.ON, MASK_MODES.STRICT];
    const currentIndex = modes.indexOf(maskMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setMaskMode(nextMode);
  };

  const unmaskId = (id) => {
    if (maskMode === MASK_MODES.ON) {
      // Add to unmasked set
      setUnmaskedIds(prev => new Set([...prev, id]));

      // Clear any existing timeout for this ID
      if (timeouts.has(id)) {
        clearTimeout(timeouts.get(id));
      }

      // Set timeout to re-mask after 3 seconds
      const timeout = setTimeout(() => {
        setUnmaskedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        setTimeouts(prev => {
          const newMap = new Map(prev);
          newMap.delete(id);
          return newMap;
        });
      }, 3000);

      setTimeouts(prev => new Map(prev).set(id, timeout));
    }
  };

  const isUnmasked = (id) => {
    return unmaskedIds.has(id);
  };

  const value = {
    maskMode,
    setMaskMode,
    toggleMaskMode,
    unmaskId,
    isUnmasked,
    isActive: maskMode !== MASK_MODES.OFF
  };

  return (
    <PrivacyMaskContext.Provider value={value}>
      {children}
    </PrivacyMaskContext.Provider>
  );
};

export const usePrivacyMask = () => {
  const context = useContext(PrivacyMaskContext);
  if (!context) {
    throw new Error('usePrivacyMask must be used within a PrivacyMaskProvider');
  }
  return context;
};
